/**
 * Organizer-uploaded raceline overlay.
 *
 * Renders inside an existing <MapContainer>; mounts below the device
 * trails (parent must order children with this first) so per-device
 * polylines draw on top.
 *
 * Drawn as a CASED double stroke — a wide dark outline under a
 * narrower slate surface — so the circuit reads as a track ribbon on
 * the light OSM tiles instead of the old 3 px dashed reference line.
 * Casing was chosen over a true geometric centerline offset on
 * purpose: offset polylines self-intersect at hairpins and need
 * projection care, while two strokes give the same double-line look at
 * every zoom for free. The geometry itself is already Catmull-Rom
 * smoothed + resampled by useEventRaceline, so corners curve instead
 * of kinking.
 */

import React from 'react';

type LeafletModule = typeof import('leaflet');
type ReactLeafletModule = typeof import('react-leaflet');

interface RacelineOverlayProps {
  modules: { L: LeafletModule; RL: ReactLeafletModule };
  coords: Array<[number, number]>;
  /** Opacity overrides for embedding contexts. The CircuitMapEditor
   *  mounts this same ribbon UNDER its editing affordances as a
   *  what-you-see-is-what-you-get preview, dialed down so the OSM
   *  detail and the vertex handles stay readable through it. */
  casingOpacity?: number;
  surfaceOpacity?: number;
}

const RacelineOverlay: React.FC<RacelineOverlayProps> = ({
  modules,
  coords,
  casingOpacity = 0.9,
  surfaceOpacity = 0.95,
}) => {
  const { Polyline } = modules.RL as any;
  if (coords.length < 2) return null;
  return (
    <>
      <Polyline
        positions={coords}
        pathOptions={{
          color: '#1e293b',
          weight: 10,
          opacity: casingOpacity,
          lineCap: 'round',
          lineJoin: 'round',
          interactive: false,
        }}
      />
      <Polyline
        positions={coords}
        pathOptions={{
          color: '#64748b',
          weight: 5,
          opacity: surfaceOpacity,
          lineCap: 'round',
          lineJoin: 'round',
          interactive: false,
        }}
      />
    </>
  );
};

export default RacelineOverlay;
